import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthz } from '../store';
import { allows } from '../api/authz';
import { locate } from './layout/menus';
import { DeniedPage } from './PermissionRoute';
import { abortLiveReads } from '../api/request';

/** currentPathname 读浏览器当前地址的路由路径 (HashRouter: 取 # 后、? 前的一段). */
function currentPathname(): string {
    const hash = window.location.hash.replace(/^#/, '');
    return hash.split('?')[0] || '/';
}

/**
 * 路由级 RBAC 守卫 (2026-09-12): 与侧栏同一口径 —— 当前路径归属的菜单 / 子菜单声明了 (resource, verb),
 * 当前角色不满足就整页替换为「无权访问」(后端同样会拦, 前端先拦是为了不出现空白页 + 一串 20104).
 * 菜单表里找不到归属的路径 (跳转页 / 我的工单等) 放行, 由各自页面的 PermissionRoute 兜底.
 */
const RouteGuard = ({ children }: { children: React.ReactNode }) => {
    const { pathname } = useLocation();
    const authz = useAuthz();
    // 离开一个页面就取消它还没回来的读请求 (2026-09-18):
    // 线上是 HTTP/2, 慢请求不会占满浏览器连接, 但**后端那条查询会一直跑** —— 数据资产
    // 几个页面直连外部源库, 一条全表扫能占住分析库的 IO 与 buffer pool, 用户早切走了它
    // 还在拖别人。cleanup 在 pathname 变化时执行, 正好是"离开本页"的时机。
    // 只取消读, 写请求不在 liveReads 里 (见 request.ts 的批注)。
    //
    // ⚠ cleanup 里必须再确认一次"地址真的变了"。<StrictMode> 下 React 会把 effect 做
    // mount → cleanup → mount 双调用 (dev 构建的 React 才有此行为, dev 环境前端是
    // build-development 打的, 所以只在 dev 复现): 页面刚挂载就跑一次 cleanup, 会把本页
    // 自己刚发出的那批读请求当场掐掉 —— 表现为整页请求 (canceled)、报"请求超时 (30s)"。
    // 真正的跳转发生时 location 已经是新地址 (router 先改 hash 再渲染), 双调用时则没变,
    // 以此区分。
    React.useEffect(
        () => () => {
            if (currentPathname() !== pathname) abortLiveReads();
        },
        [pathname],
    );
    if (!authz.loaded) {
        return <div className="text-sm text-slate-500">正在校验权限…</div>;
    }
    const loc = locate(pathname);
    if (loc) {
        const sub = loc.menu.subs[loc.subIdx];
        const can = (resource?: string, verb = 'get') =>
            !resource || allows(authz.rules, resource, verb);
        // anyOf: 一个页面承载多个资源域时, 满足任一条即可进 (如数据字典按各组归属分权)
        const canItem = (m?: {
            resource?: string;
            verb?: string;
            anyOf?: { resource: string; verb?: string }[];
        }) =>
            m?.anyOf
                ? m.anyOf.some((x) =>
                      allows(authz.rules, x.resource, x.verb ?? 'get'),
                  )
                : can(m?.resource, m?.verb);
        if (!canItem(loc.menu) || !canItem(sub)) {
            return <DeniedPage roles={authz.roles} />;
        }
    }
    return <>{children}</>;
};

export default RouteGuard;
